import {
  CompanyInputType,
  ContactInputType,
  InputType,
} from "../constants/InputType.constant";
import {
  FormColumnWidth,
  FormColumnWidthValue,
} from "../constants/FormColumnWidth.constant";
import { PositionType } from "../constants/PositionType.constant";
import { FormInput } from "../interfaces/api/get-web-form";
import {
  FormInputCategoryOption,
  FormInputOption,
  FormInputOptions,
} from "../interfaces/formInputOptions";
import { isEmail } from "../../../ui/utils/stringUtils";

export const CONTACT_EMAIL_TYPE = ContactInputType.CONTACT_EMAIL.id;
export const SELECT_TYPE = InputType.SELECT.id;
export const CATEGORY_SELECT_TYPE = InputType.CATEGORY_SELECT.id;
export const CHECKBOX_TYPE = InputType.CHECKBOX.id;
export const ACCEPTANCE_TYPE = InputType.ACCEPTANCE.id;
export const CATEGORY_VALUE_SEP = "::";

export type FormHtmlInputType = "text" | "email" | "number" | "textarea";

export function getFormInputHtmlType(type: string): FormHtmlInputType {
  switch (type) {
    case ContactInputType.CONTACT_NAME.id:
    case InputType.TEXT.id:
    case CompanyInputType.COMPANY_NAME.id:
      return "text";
    case ContactInputType.CONTACT_EMAIL.id:
      return "email";
    case InputType.NUMBER.id:
    case ContactInputType.CONTACT_PHONE.id:
      return "number";
    case InputType.LONG_TEXT.id:
      return "textarea";
    case InputType.SELECT.id:
    case InputType.CATEGORY_SELECT.id:
    case InputType.CHECKBOX.id:
    case InputType.ACCEPTANCE.id:
      return "text";
    default:
      return "text";
  }
}

export function isFormInputCategoryOption(
  option: unknown
): option is FormInputCategoryOption {
  return (
    typeof option === "object" &&
    option !== null &&
    "label" in option &&
    "subcategories" in option
  );
}

export function normalizeFormInputOptions(options?: FormInputOptions): string[] {
  if (!Array.isArray(options)) return [];
  return options
    .filter((option: FormInputOption): option is string => typeof option === "string")
    .map((option) => option.trim())
    .filter(Boolean);
}

export function normalizeCategoryOptions(
  options?: FormInputOptions
): FormInputCategoryOption[] {
  if (!Array.isArray(options)) return [];
  return options
    .filter(isFormInputCategoryOption)
    .map((option: FormInputCategoryOption) => ({
      label: String(option.label || "").trim(),
      subcategories: (option.subcategories || [])
        .map((subcategory: string) => String(subcategory || "").trim())
        .filter(Boolean),
    }))
    .filter((option) => option.label);
}

export function hasCategoryOptions(options?: FormInputOptions): boolean {
  return normalizeCategoryOptions(options).length > 0;
}

export function getCategorySelectPlaceholder(
  placeholder?: string,
  categories: FormInputCategoryOption[] = []
): string {
  const trimmed = String(placeholder || "").trim();
  if (trimmed) return trimmed;
  return categories[0]?.label || "";
}

export function splitFormInputOptions(raw: string): string[] {
  return raw
    .split(/\r?\n|,/)
    .map((option) => option.trim())
    .filter(Boolean);
}

export function isChoiceFormInput(type: string): boolean {
  return type === SELECT_TYPE;
}

export function isCategoryFormInput(type: string): boolean {
  return type === CATEGORY_SELECT_TYPE;
}

export function isBooleanFormInput(type: string): boolean {
  return type === CHECKBOX_TYPE || type === ACCEPTANCE_TYPE;
}

export function parseCategoryFieldValue(value?: string): {
  category: string;
  subcategory: string;
} {
  const [category = "", subcategory = ""] = String(value || "").split(
    CATEGORY_VALUE_SEP
  );
  return { category, subcategory };
}

export function formatCategoryFieldValue(
  category: string,
  subcategory: string
): string {
  return `${category}${CATEGORY_VALUE_SEP}${subcategory}`;
}

export function formatCategoryDisplayValue(value?: string): string {
  const { category, subcategory } = parseCategoryFieldValue(value);
  if (!category) return "";
  if (!subcategory) return category;
  return `${category} > ${subcategory}`;
}

export function formatFormResponseDisplayValue(
  value?: string,
  type?: string
): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "";

  if (type === CATEGORY_SELECT_TYPE || raw.includes(CATEGORY_VALUE_SEP)) {
    return formatCategoryDisplayValue(raw);
  }
  if (
    type === CHECKBOX_TYPE ||
    type === ACCEPTANCE_TYPE ||
    raw === "true" ||
    raw === "false"
  ) {
    return raw === "true" ? "Sí" : "No";
  }
  return raw;
}

export function parseFormResponsePayload(
  response?: string | Record<string, string> | null
): Record<string, string> {
  if (!response) return {};
  let parsed: unknown = response;
  if (typeof response === "string") {
    try {
      parsed = JSON.parse(response);
    } catch {
      return {};
    }
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return {};
  }

  return Object.entries(parsed as Record<string, unknown>).reduce(
    (acc, [key, value]) => {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        Object.entries(value as Record<string, unknown>).forEach(
          ([nestedKey, nestedValue]) => {
            const display = formatFormResponseDisplayValue(
              String(nestedValue ?? "")
            );
            if (display) acc[nestedKey] = display;
          }
        );
        return acc;
      }

      const display = formatFormResponseDisplayValue(String(value ?? ""));
      if (display) acc[key] = display;
      return acc;
    },
    {} as Record<string, string>
  );
}

export function dedupeContactFormResponses<
  T extends {
    id: number;
    formId?: number;
    formTitle: string;
    createdAt: string;
  }
>(responses: T[]): T[] {
  if (!Array.isArray(responses) || responses.length <= 1) {
    return responses || [];
  }

  const sorted = [...responses].sort(
    (left, right) =>
      new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime()
  );
  const seen = new Set<string>();

  return sorted.filter((item) => {
    const key =
      item.formId != null ? `form:${item.formId}` : `title:${item.formTitle}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function isCategoryFieldComplete(value?: string): boolean {
  const { category, subcategory } = parseCategoryFieldValue(value);
  return Boolean(category.trim() && subcategory.trim());
}

export function getFormButtonJustify(position?: string | number): string {
  switch (String(position ?? "")) {
    case PositionType.LEFT.id:
      return "flex-start";
    case PositionType.MIDDLE.id:
      return "center";
    case PositionType.RIGHT.id:
      return "flex-end";
    default:
      return "center";
  }
}

export function getFormButtonWidth(buttonSize?: number): number {
  return Math.max(140, Number(buttonSize || 0));
}

export function isFormFieldInvalid(
  input: Pick<FormInput, "id" | "type" | "required">,
  value: string
): boolean {
  if (isBooleanFormInput(input.type)) {
    return input.required && value !== "true";
  }
  if (isCategoryFormInput(input.type)) {
    return input.required && !isCategoryFieldComplete(value);
  }
  if (input.required && !String(value || "").trim()) return true;
  if (input.type === CONTACT_EMAIL_TYPE && value && !isEmail(value)) return true;
  return false;
}

export function collectFormInputErrors(
  inputs: Array<
    Pick<FormInput, "id" | "type" | "required"> & { value?: string }
  >
): string[] {
  const errors: string[] = [];
  inputs.forEach((input) => {
    if (isFormFieldInvalid(input, input.value || "")) {
      errors.push(input.id);
    }
  });
  return errors;
}

export function mergeFormInputErrors(
  currentErrors: string[],
  inputId: string,
  invalid: boolean
): string[] {
  if (invalid) {
    return currentErrors.includes(inputId)
      ? currentErrors
      : [...currentErrors, inputId];
  }
  return currentErrors.filter((error) => error !== inputId);
}

export function normalizeColumnWidth(
  columnWidth?: string
): FormColumnWidthValue {
  return columnWidth === FormColumnWidth.HALF
    ? FormColumnWidth.HALF
    : FormColumnWidth.FULL;
}

export function getFormInputColumnClass(columnWidth?: string): string {
  return normalizeColumnWidth(columnWidth) === FormColumnWidth.HALF
    ? "web-form-fields__group--half"
    : "web-form-fields__group--full";
}

export function getFormInputCheckColumnClass(columnWidth?: string): string {
  return normalizeColumnWidth(columnWidth) === FormColumnWidth.HALF
    ? "web-form-fields__check-group--half"
    : "web-form-fields__check-group--full";
}

const ACCEPTANCE_TERMS_URL_PREFIX = "termsUrl:";
const ACCEPTANCE_PRIVACY_URL_PREFIX = "privacyUrl:";

export interface AcceptanceLinkUrls {
  termsUrl?: string;
  privacyUrl?: string;
}

export function getAcceptanceLinkUrls(
  options?: FormInputOptions
): AcceptanceLinkUrls {
  const strings = normalizeFormInputOptions(options);
  const result: AcceptanceLinkUrls = {};

  strings.forEach((entry) => {
    if (entry.startsWith(ACCEPTANCE_TERMS_URL_PREFIX)) {
      result.termsUrl = entry.slice(ACCEPTANCE_TERMS_URL_PREFIX.length).trim();
    }
    if (entry.startsWith(ACCEPTANCE_PRIVACY_URL_PREFIX)) {
      result.privacyUrl = entry
        .slice(ACCEPTANCE_PRIVACY_URL_PREFIX.length)
        .trim();
    }
  });

  return result;
}

export function setAcceptanceLinkUrls(
  options: FormInputOptions | undefined,
  urls: AcceptanceLinkUrls
): FormInputOptions {
  const preserved = (options || []).filter((entry) => {
    if (typeof entry !== "string") return true;
    return (
      !entry.startsWith(ACCEPTANCE_TERMS_URL_PREFIX) &&
      !entry.startsWith(ACCEPTANCE_PRIVACY_URL_PREFIX)
    );
  });

  const next: FormInputOptions = [...preserved];
  const termsUrl = urls.termsUrl?.trim();
  const privacyUrl = urls.privacyUrl?.trim();

  if (termsUrl) next.push(`${ACCEPTANCE_TERMS_URL_PREFIX}${termsUrl}`);
  if (privacyUrl) next.push(`${ACCEPTANCE_PRIVACY_URL_PREFIX}${privacyUrl}`);

  return next;
}
